import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  find,
  get,
  isEmpty,
  uniqueId,
} from 'lodash';
import {
  API,
  // ExternalIdHelper,
  FieldGroupContainer,
  ToastActions,
  VibeModal,
} from 'vibeguide';
import ProfileExternalIDRow from './ProfileExternalDRow';
import './ProfileExternalIds.scss';

function ProfileExternalIds({
  className = '',
  style = {},
  externalIds = [],
  locationId = '',
  setHasChanges,
  setNewExternalIDs,
  newExternalIDs = [],
  onUpdate,
  updateCurrentLocation,
  user,
  queueToast,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [extIdToDelete, setExtIdToDelete] = useState({});
  const [externalIDsList, setExternalIDsList] = useState([{
    label: '',
    value: '',
  }]);

  const canManageExternalIds = user.can('location.manage_external_ids');

  const checkExternalIDErrors = (newexternalids) => {
    // set errors for unfilled fields
    const updatedExtIdsErrors = newexternalids.map((extid) => {
      extid.extIdError = false;
      extid.sourceError = false;
      if (isEmpty(extid.extId) && !isEmpty(extid.source)) {
        extid.extIdError = true;
      }
      if (!isEmpty(extid.extId) && isEmpty(extid.source)) {
        extid.sourceError = true;
      }
      return extid;
    });
    return updatedExtIdsErrors;
  };

  useEffect(() => {
    if (newExternalIDs.length > 0) {
      if (newExternalIDs.some(extID => isEmpty(extID.extId) || isEmpty(extID.source))) {
        setHasChanges(false);
      } else {
        setHasChanges(true);
      }
      if (newExternalIDs.some(newExtId => newExtId.error)) {
        setNewExternalIDs(newExternalIDs.filter(newExtId => newExtId.error).map(newextid => {
          delete newextid.error;
          newextid.showError = true;
          return newextid;
        }));
      }
    } else {
      setHasChanges(false);
    }
  }, [newExternalIDs]);

  useEffect(() => {
    if (externalIds.length > 0) {
      const filteredSavingExtIds = newExternalIDs.filter(extid => {
        if (extid.saving) {
          if (externalIds.some(extid1 => extid.extId === extid1.extId && extid.source === extid1.source)) {
            return false;
          }
        }
        return true;
      });
      setNewExternalIDs(filteredSavingExtIds);
    }
  }, [externalIds]);

  useEffect(() => {
    async function fetchData() {
      const response = await API.Location.getExternalIDs();
      const extIDsOptions = response.map(extid => {
        if (extid.key === 'vibenomics') {
          return {
            label: extid.name,
            value: extid.key,
            disabled: true,
          };
        }
        return {
          label: extid.name,
          value: extid.key,
        };
      });
      extIDsOptions.unshift({
        label: '',
        value: '',
      });
      setExternalIDsList(extIDsOptions);
    }
    fetchData();
  }, []);

  /**
   * Change an new External Source value
   */
  const onChangeNewExternalSource = (e) => {
    const {
      target: {
        value,
        dataset: {
          id,
        },
      },
    } = e;

    const updatedExternalIDs = newExternalIDs.map((newExternalID) => {
      if (newExternalID._id === id) {
        newExternalID.source = value;
        return newExternalID;
      }

      return newExternalID;
    });

    setNewExternalIDs(checkExternalIDErrors(updatedExternalIDs));
    setHasChanges(true);
  };

  /**
   * Change an new External ID value
   */
  const onChangeNewId = (e) => {
    const {
      target: {
        value,
        dataset: {
          id,
        },
      },
    } = e;

    const updatedExternalIDs = newExternalIDs.map((newExternalID) => {
      if (newExternalID._id === id) {
        newExternalID.extId = value;
        return newExternalID;
      }

      return newExternalID;
    });

    setNewExternalIDs(checkExternalIDErrors(updatedExternalIDs));
    setHasChanges(true);
  };

  /**
   * Add new External Source/ID field row
   */
  const onAddExternalIDRow = () => {
    setNewExternalIDs([
      ...newExternalIDs,
      {
        _id: uniqueId('external-id-'),
        source: '',
        extId: '',
      },
    ]);
  };

  /**
   * Delete a current or new External Source/ID field pair
   */
  const onRemoveExternalId = (extId, source, index) => {
    if (index !== undefined) {
      if (newExternalIDs.length === 1 && index === 0) {
        setNewExternalIDs([]);
      } else {
        setNewExternalIDs(newExternalIDs.filter((id, i) => i !== index));
      }
    } else {
      const externalId = find(externalIds, { source, extId });

      if (externalId) {
        setExtIdToDelete(externalId);
        setShowDeleteModal(true);
      }
    }
  };

  /**
   * Click Delete External ID in confirm modal
   */
  const onConfirmRemoveExternalId = async () => {
    const response = await API.Location.deleteExternalID({
      id: locationId,
      source: extIdToDelete.source,
      extid: extIdToDelete.extId,
    });

    const success = get(response, '[0].type') === 'LOCATION.EXTERNAL_ID_DELETED';

    if (success) {
      queueToast({
        type: 'success',
        title: 'External ID Deleted!',
        allowClose: true,
        delay: 500,
      });

      const locationObj = await API.Location.getById(locationId);
      onUpdate(locationObj);
      updateCurrentLocation(locationObj);
      setShowDeleteModal(false);
    }
  };

  /**
   * Close Delete modal
   */
  const onCloseRemoveExternalId = () => {
    setShowDeleteModal(false);
  };

  return (
    <div
      className={classNames('ProfileExternalIds', className)}
      style={style}
    >
      <div className={classNames('edit-section')}>
        <FieldGroupContainer
          addRowLabel="Add External Source ID"
          allowAddRow={canManageExternalIds}
          onAddRow={onAddExternalIDRow}
        >
          {externalIds.map((item, i) => {
            if (item.source === 'vibenomics') {
              return (
                <ProfileExternalIDRow
                  key={item._id}
                  id={item._id}
                  source={item.source}
                  extId={item.extId}
                  error={item.showError}
                  item={item}
                  disableInput
                  showLabels={i === 0}
                  options={externalIDsList}
                  alignColumns={canManageExternalIds}
                  onRemove={onRemoveExternalId}
                />
              );
            }

            return (
              <ProfileExternalIDRow
                key={item._id}
                id={item._id}
                source={item.source}
                extId={item.extId}
                error={item.showError}
                item={item}
                showLabels={i === 0}
                options={externalIDsList}
                allowRemove={canManageExternalIds}
                disableInput
                onRemove={onRemoveExternalId}
              />
            );
          })}

          {canManageExternalIds && newExternalIDs.map((item, i) => (
            <ProfileExternalIDRow
              key={item._id}
              id={item._id}
              source={item.source}
              extId={item.extId}
              error={item.showError}
              item={item}
              index={i}
              disableInput
              isNew
              options={externalIDsList}
              allowRemove={canManageExternalIds}
              onChangeSource={onChangeNewExternalSource}
              onChangeId={onChangeNewId}
              onRemove={onRemoveExternalId}
              dataId={i.toString()}
            />
          ))}
        </FieldGroupContainer>
      </div>

      <VibeModal
        show={showDeleteModal}
        type="confirm"
        confirmProps={{
          color: 'error',
        }}
        title="You are deleting an External Source ID"
        text="Deleting will remove its reference to this system."
        onConfirm={onConfirmRemoveExternalId}
        onClose={onCloseRemoveExternalId}
      />
    </div>
  );
}

ProfileExternalIds.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  externalIds: PropTypes.arrayOf(PropTypes.object),
  locationId: PropTypes.string,
  setHasChanges: PropTypes.func.isRequired,
  setNewExternalIDs: PropTypes.func.isRequired,
  newExternalIDs: PropTypes.arrayOf(PropTypes.object),
  onUpdate: PropTypes.func.isRequired,
  updateCurrentLocation: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileExternalIds);
